import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { kitT, type KitTranslateFn } from '../../lib/kitTranslate';
import { Progress } from '../ui/progress';

export interface TimerProps {
    duration: number; // seconds
    onTimeUp?: () => void;
    onTick?: (remaining: number) => void;
    warningThreshold?: number; // seconds remaining to show warning
    paused?: boolean;
    className?: string;
    showProgress?: boolean;
    translate?: KitTranslateFn;
}

export const Timer: React.FC<TimerProps> = ({
    duration,
    onTimeUp,
    onTick,
    warningThreshold = 30,
    paused = false,
    className,
    showProgress = true,
    translate,
}) => {
    const [remaining, setRemaining] = useState(duration);
    const [isWarning, setIsWarning] = useState(false);

    const formatTime = useCallback((seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }, []);

    useEffect(() => {
        if (paused) return;

        const interval = setInterval(() => {
            setRemaining((prev) => {
                const newValue = prev - 1;

                if (newValue <= warningThreshold && !isWarning) {
                    setIsWarning(true);
                }

                onTick?.(newValue);

                if (newValue <= 0) {
                    clearInterval(interval);
                    onTimeUp?.();
                    return 0;
                }

                return newValue;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [paused, warningThreshold, isWarning, onTick, onTimeUp]);

    const progress = (remaining / duration) * 100;
    const timeLabel = formatTime(remaining);
    const ariaRemaining = kitT(translate, 'kTmAriaRemaining', 'Time remaining: {time}').replace(
        '{time}',
        timeLabel
    );

    return (
        <div
            className={cn(
                'flex items-center gap-3 rounded-xl px-4 py-2 transition-all duration-200',
                isWarning ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground',
                className
            )}
            role="timer"
            aria-live="polite"
            aria-label={ariaRemaining}
        >
            {isWarning ? (
                <AlertTriangle className="h-5 w-5 animate-pulse" />
            ) : (
                <Clock className="h-5 w-5" />
            )}

            <div className="flex-1">
                {showProgress && (
                    <Progress
                        value={progress}
                        className={cn('h-2 mb-1', isWarning && '[&>div]:bg-destructive')}
                    />
                )}
                <span
                    className={cn(
                        'text-lg font-bold font-heading tabular-nums',
                        isWarning && 'animate-pulse'
                    )}
                >
                    {timeLabel}
                </span>
            </div>
        </div>
    );
};

Timer.displayName = 'Timer';
